
import {defineComponent} from "vue";
import {Bar, Doughnut} from "vue-chartjs";
import axios from "axios";
import {Chart, registerables} from "chart.js";
import SliderResult from "./components/results/SliderResult.vue";
import RightWrongBar from "./components/results/RightWrongBar.vue";

Chart.register(...registerables);

const MAIN_CHART_OPTIONS = {
  responsive: true,
  animation: {duration: 1500, delay: 500},
  maintainAspectRatio: false,
  height: 600,
  layout: {
    padding: 30 // Padding around the chart
  },
  cutout: '30%',
  plugins: {
    legend: {
      display: false,
      position: 'bottom',
      font: {
        family: 'Heebo',
      }
    },
    datalabels: {
      display: true,
      color: 'black',
      font: {
        weight: 'bold',
      },
      formatter: (value) => value,
    },
  },
};

export default defineComponent({
  name: "SCIRTResults",
  components: {
    RightWrongBar,
    SliderResult,
    Bar,
    Doughnut,
  },
  data() {
    return {
      numericValues: {},
      rightWrongData: {},
      chartReady: false,
      processedCharts: [],
      triviaData: [],
      chartOptions: MAIN_CHART_OPTIONS,
      horizontalChartOptions: {
        ...MAIN_CHART_OPTIONS,
        indexAxis: "y",
        scales: {
          x: {beginAtZero: true},
          // y: {display: false}
        },
        plugins: {
          ...MAIN_CHART_OPTIONS.plugins,
          // legend: {
          //   ...MAIN_CHART_OPTIONS.plugins.legend,
          //   display: true, position: "bottom"
          // }
        },
      },
      sourceLink: "https://scirt.prospex-institute.org",
      source: "scirt.prospex-institute.org",
      chartColors: {
        primary: ["#EBCFCB", "#8BE0B9"],
        dark: ["#e0bab6", "#69bb96"],
        light: ["#F1E6E4", "#D7F4E7"],
      },

      questions: [
        // Last shopping
        {question: "shopping", type: "single_option"},
        {question: "BuyingCriteria", type: "sorted_options"},
        {question: "HowMuchSpendOnJeans", type: "numeric_values"},
        {question: "WhatToPayForPants", type: "numeric_values"},
        {question: "HowMuchSwimmwear", type: "numeric_values"},
        {question: "ShoppingHabits.question1", type: "likart_scale_values"},
        {question: "ShoppingHabits.question2", type: "likart_scale_values"},
        {question: "ShoppingHabits.question3", type: "likart_scale_values"},
        {question: "ShoppingHabits.question4", type: "likart_scale_values"},
        {question: "ShoppingHabits.question5", type: "likart_scale_values"},
        {question: "HowOftenConsiderSustainability", type: "likart_scale_values"},
        {question: "DragDropShirt", type: "single_option"},
        {question: "Emissions", type: "single_option"},
        {question: "HowManyPeopleEmployed", type: "single_option"},
        {question: "NotSoldGarments", type: "single_option"},
        {question: "HowMuchClothesBoughtKg", type: "single_option"},
        {question: "WhereDoYouBuyClothes", type: "likart_scale_values"},
        {question: "HowManyProduced", type: "single_option"},
        {question: "WaterNeededForShirt.answer", type: "single_option"},
      ]
    };
  },
  mounted() {
    this.loadData();
  },

  computed: {},

  methods: {
    loadData() {
      axios
          .post("https://portal.prospex-institute.org/pitool/charts", {
            source: this.source,
            locale: "en",
            questions: this.questions,
          })
          .then((response) => {
            const {results, texts} = response.data;
            this.processCharts(results, texts.en);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.chartReady = true;
          });
    },
    processCharts(results, texts) {
      this.processedCharts = {};

      this.processCombinedLikardQuestions(
          Array.from(results ?? []).filter((item: any) => item.question.startsWith('ShoppingHabits.')),
          texts
      );

      Array.from(results ?? []).forEach((item: any) => {
        switch (item.question) {

          case "WhereDoYouBuyClothes": {
            const labels = ["Online", "Mostly online", "Equaly", "Mostly offline", "Offline"];

            let resultData = Object.entries(this.resultToPercentage(item)).map(([key, value]) => [parseInt(key), value]);
            resultData = Object.fromEntries(resultData);

            const data = [];
            labels.forEach((label, index) => {
              texts[index] = label;
              data.push(resultData[index + 1] || 0);
            });

            const chart = this.createHorizontalBarChart(texts[item.question], labels, data, texts, (value) => ` ${value.toFixed(1)}%`);

            this.processedCharts[item.question] = {
              item, ...chart
            };
            return;
          }

          case 'BuyingCriteria': {
            const chart = this.createHorizontalBarChart(
                texts[item.question],
                Object.keys(item.result.average_position).map((answer) => texts[answer] || answer),
                Object.values(item.result.average_position).map((position: number) => (6 - position)),
                texts
            );

            // min 1, max 5
            chart.options = {
              ...chart.options,
              scales: {
                x: {
                  min: 1,
                  max: 5,
                  title: {
                    display: true,
                    text: 'Average position in the list between 1 and 6'
                  },
                },
              },
            };

            this.processedCharts[item.question] = {
              item, ...chart
            };
            return;
          }
        }

        if (['HowManyProduced',
          "WaterNeededForShirt.answer",
          "HowManyPeopleEmployed", "NotSoldGarments", "HowMuchClothesBoughtKg",
          "Emissions"].includes(item.question)) {
          this.processRightWrongChart(item, texts);
          return;
        }


        switch (item.type) {
          case "single_option":
            this.processedCharts[item.question] = {
              item, ...this.createDoughnutChart(
                  item,
                  Object.keys(item.result).map((answer) => texts[answer] || answer),
                  // Percentage of answers
                  Object.values(this.resultToPercentage(item)),
                  texts,
                  (value) => ` ${value}%`
              )
            };
            return;


          case "sorted_options":
            this.processedCharts[item.question] = {
              item, ...this.createStackedBarChart(
                  texts[item.question],
                  Object.keys(item.result.distribution),
                  item.result.distribution,
                  texts,
                  (value) => ` ${value}%`
              )
            };
            return;


          case "numeric_values":
            this.numericValues[item.question] = {
              ...item.result,
              title: texts[item.question] || item.question,
              count: item.count,
              item,
            };
            return;

          case "likart_scale_values":
            // bar chart
            this.processedCharts[item.question] = {
              item, ...this.createHorizontalBarChart(
                  texts[item.question],
                  Object.keys(item.result).map((answer) => texts[answer] || answer),
                  Object.values(this.resultToPercentage(item)),
                  texts,
                  (value) => ` ${value}%`
              )
            };
            return;
        }
      });


    },


    processRightWrongChart(item, texts) {
      const rightAnswers = {
        'HowManyProduced': '100-150',
        'WaterNeededForShirt.answer': '2700',
        'HowManyPeopleEmployed': '300',
        'NotSoldGarments': '30',
        'HowMuchClothesBoughtKg': '26',
        'Emissions': '2-5',
      }

      const rightPercentage = this.percentage(item.result[rightAnswers[item.question]], item.count);

      this.rightWrongData[item.question] = {
        count: item.count,
        right: rightPercentage,
        wrong: 100 - rightPercentage
      }

      console.log(this.rightWrongData);
    },

    resultToPercentage(item) {
      // sum answers and return percentage
      if (!item || !item.count) return 0;

      return Object.entries(item.result)
          .map(([key, value]) => [key, this.percentage(value, item.count)]).reduce((out, [key, val]) => ({
            ...out,
            [`${key}`]: val
          }), {});
    },

    percentage(answer, count) {
      // sum answers and return percentage
      if (!answer || !count) return 0;

      return Math.round((answer / count) * 100);
    },

    sum(answers) {
      return answers.reduce((a, b) => a + b, 0);
    },

    processCombinedLikardQuestions(items, texts) {
      let shoppingHabitsData: {
        [key: string]: { count: number, average: number }
      } = {};

      const shoppingHabitsIndexes = {
        never: 1,
        rarely: 2,
        sometimes: 3,
        often: 4,
        always: 5,
      };

      for (let item of items) {
        // combine all shopping habits questions likar scales into one chart, were we take only average for each question
        shoppingHabitsData[item.question] = {
          count: item.count,
          average: this.sum(Object.entries(item.result).map(([key, value]) => (shoppingHabitsIndexes[key] || 0) * (value as number))) / (item.count || 1)

        };
      }

      const shoppingDataChart = this.createHorizontalBarChart(
          'Shopping Habits',
          Object.keys(shoppingHabitsData).map((key) => texts[key] || key),
          Object.values(shoppingHabitsData).map((item) => item.average),
          texts
      );

      // shoppingDataChart.data.datasets[0].label = 'Score between 1 and 5';
// min - 1, max - 5
      shoppingDataChart.options = {
        ...shoppingDataChart.options,
        scales: {
          ...shoppingDataChart.options.scales,
          x: {
            ...shoppingDataChart.options.scales.x,
            min: 1,
            max: 5,
            title: {
              display: true,
              text: 'Score between 1 and 5. 1 - Never, 5 - Always'
            },
          },
        },
      };

      const totalCount = Object.values(shoppingHabitsData).reduce((sum, item) => sum + item.count, 0);

      this.processedCharts['ShoppingHabits'] = {
        item: {question: 'ShoppingHabits', count: totalCount},
        ...shoppingDataChart
      };
    },
    createStackedBarChart(title, criteria, distribution, texts, formatLabel = (value) => value) {
      const datasets = Object.keys(distribution[criteria[0]]).map((key, index) => ({
        // label: key,
        data: criteria.map((criterion) => distribution[criterion][key]),
        backgroundColor: this.getHorizontalChartColor(index),
      }));

      // const chartLabels = datasets.map((data, index) => ({
      //   label: `${data.label} - ${formatLabel(data.data[0])}`, color: data.backgroundColor
      // }));

      return {
        title,
        component: "Bar",
        data: {
          labels: criteria.map((criterion) => texts[criterion]),
          datasets,
        },
        options: {
          ...this.horizontalChartOptions,
          plugins: {
            legend: {display: true},
            datalabels: {
              formatter: formatLabel
            }
          },
          scales: {
            x: {stacked: true},
            y: {stacked: true},
          },
        },
      };
    },
    createDoughnutChart(item, labels, data, texts, formatLabel = (value) => value) {
      // sort data and labels according to data
      const sortedData = data.map((value, index) => ({value, index})).sort((a, b) => b.value - a.value);
      const sortedLabels = sortedData.map(({value, index}) => {
        const labelIndex = data.findIndex((v, i) => v === value && !sortedData.slice(0, index).some(d => d.index === i));
        return labels[labelIndex];
      });

      const labelColors = this.incrementalChartColors(data.length);
      const chartLabels = sortedLabels.map((label, index) => ({
        label: texts[label] || label, color: labelColors[index]
      }));

      return {
        title: texts[item.question],
        component: "Doughnut",
        chartLabels,
        data: {
          // labels: sortedLabels,
          nCount: item.count,
          datasets: [
            {
              data: sortedData.map(({value}) => value),
              backgroundColor: this.incrementalChartColors(data.length),
              spacing: 1,
            },
          ],
        },
        options: {
          ...this.chartOptions, plugins: {
            ...this.chartOptions.plugins,
            datalabels: {formatter: formatLabel}
          }
        },
      };
    },

    createBarChart(title, labels, data) {
      return {
        title,
        component: "Bar",
        data: {
          labels: labels,
          datasets: [
            {
              data,
              label: 'Answers',
              backgroundColor: this.incrementalChartColors(data.length),
            },
          ],
        },
        options: {...this.chartOptions},
      };
    },

    createHorizontalBarChart(title, labels, data, texts, formatValueFn = null) {
      const formatValue = formatValueFn || this.genericFormatValue;

      const datasets = Array.from(data).map((value, index) => ({
        data: [value],
        label: labels[index],
        backgroundColor: this.incrementalChartColors(data.length)[index],
      }));

      const chartLabels = datasets.map((data, index) => ({
        label: `${labels[index]} - ${formatValue(data.data[0])}`, color: data.backgroundColor
      }));

      return {
        component: "Bar",
        chartLabels,
        data: {
          labels: ['#'],
          datasets,
        },
        options: {
          ...this.horizontalChartOptions,
          plugins: {
            ...this.horizontalChartOptions.plugins,
            datalabels: {
              formatter: formatValue
            }
          }
        },
      };
    },

    genericFormatValue(value) {
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return value.toFixed(1);

      return value;
    },

    getRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    getHorizontalChartColor(position) {
      // alternate project colors
      return position % 2 === 0 ? this.chartColors.primary : this.chartColors.dark;
    },

    incrementalChartColors(count = 4) {

      // range of colors between two values in this.chartColors.primary
      const colorBetweenColors = (color1, color2, position) => {
        const hex = (color1, color2) => {
          const c1 = parseInt(color1, 16);
          const c2 = parseInt(color2, 16);
          const diff = c2 - c1;
          const delta = Math.round(diff / count);
          return (c1 + delta * position).toString(16);
        };
        return `#${hex(color1.slice(1, 3), color2.slice(1, 3))}${hex(color1.slice(3, 5), color2.slice(3, 5))}${hex(color1.slice(5, 7), color2.slice(5, 7))}`;
      };

      return Array.from({length: count}, (_, index) => colorBetweenColors(this.chartColors.light[0], this.chartColors.dark[1], index));
    },
  },
})
;
